import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Brand, BrandConfig, getBrandConfig } from './brandConfig';

const STORAGE_KEY = 'selectedBrand';

// Domaines email qui forcent une marque spécifique (restriction de visibilité)
const DOMAIN_RESTRICTIONS: Record<string, Brand> = {
  "laser-world.fr": "laser",
};

// Exceptions qui doivent pouvoir choisir la marque librement
const BRAND_RESTRICTION_EXCEPTIONS = new Set(["lucas@laser-world.fr"]);

interface BrandContextType {
  brand: Brand;
  setBrand: (brand: Brand) => void;
  brandConfig: BrandConfig;
  isRestricted: boolean; // true = switch caché, marque forcée
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: ReactNode;
  userEmail?: string | null;
}

/**
 * Détecte si l'email est restreint à une marque spécifique
 */
export function getRestrictedBrand(email?: string | null): Brand | null {
  if (!email) return null;
  const normalizedEmail = email.trim().toLowerCase();
  if (BRAND_RESTRICTION_EXCEPTIONS.has(normalizedEmail)) return null;
  const domain = normalizedEmail.split("@")[1];
  return domain ? DOMAIN_RESTRICTIONS[domain] || null : null;
}

/**
 * Provider pour le contexte de marque
 * Persiste la sélection dans localStorage
 * Peut forcer une marque selon le domaine email
 */
export function BrandProvider({ children, userEmail }: BrandProviderProps) {
  const restrictedBrand = getRestrictedBrand(userEmail);
  
  const [brand, setBrandState] = useState<Brand>(() => {
    // Si restreint par domaine email, forcer cette marque
    if (restrictedBrand) return restrictedBrand;
    
    // Sinon, lire depuis localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'smile' || stored === 'laser' || stored === 'vertical') {
        return stored;
      }
    }
    return 'smile'; // Par défaut
  });

  // Si l'utilisateur devient restreint (login), forcer la marque
  useEffect(() => {
    if (restrictedBrand && brand !== restrictedBrand) {
      setBrandState(restrictedBrand);
    }
  }, [restrictedBrand, brand]);

  // Mettre à jour le titre du document selon la marque
  useEffect(() => {
    const config = getBrandConfig(brand);
    document.title = `${config.eventName} — Web`;
  }, [brand]);

  const setBrand = (newBrand: Brand) => {
    // Ignorer si restreint
    if (restrictedBrand) return;
    
    setBrandState(newBrand);
    // Persister dans localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newBrand);
    }
  };

  const brandConfig = getBrandConfig(brand);

  return (
    <BrandContext.Provider value={{ brand, setBrand, brandConfig, isRestricted: !!restrictedBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

/**
 * Hook pour utiliser le contexte de marque
 */
export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand doit être utilisé dans un BrandProvider');
  }
  return context;
}
